// @ts-strict-ignore
import { dayjs } from '@/extendedDayjs';
import { getLocaleDateTimeFormat } from '../timeHelpers';
import { FeatureFlag, FeatureFlagPredicate, FeatureFlagRuleCondition } from '../types';
import { BaseUpdateReleaseRequest } from './release';
import { FeatureFlagUpdatable } from '@/actions/updatable';

export const FETCH_FEATURE_FLAGS_SUCCESS = 'FETCH_FEATURE_FLAGS_SUCCESS';
export const UPDATE_FEATURE_FLAGS = 'UPDATE_FEATURE_FLAGS';
export const FEATURE_FLAGS_FOR_RELEASES_UPDATED = 'FEATURE_FLAGS_FOR_RELEASES_UPDATED';

export interface FetchFeatureFlagsResponse {
    featureFlags: FeatureFlag[];
    releaseId: string; // note this is added from the request as it's needed for success handling and not part of the server response
}

export interface FetchFeatureFlagsSuccessAction {
    type: typeof FETCH_FEATURE_FLAGS_SUCCESS;
    payload: FetchFeatureFlagsResponse;
}

export interface UpdateFeatureFlagsResponse {
    releaseId: string;
    featureFlags: FeatureFlag[];
}

export interface UpdateFeatureFlagsSuccessAction {
    type: typeof UPDATE_FEATURE_FLAGS;
    payload: UpdateFeatureFlagsResponse;
}

export interface ToggleFeatureFlagRequest extends BaseUpdateReleaseRequest {
    featureFlagId: string;
    enable: boolean;
}

export type UpdateFeatureFlagsRequestDataType = ToggleFeatureFlagRequest;

interface UpdateFeatureFlagsRequestType {
    requestData: UpdateFeatureFlagsRequestDataType;
    id?: number | string;
}

export interface TryUpdateFeatureFlagsAction {
    type: FeatureFlagUpdatable;
    payload: UpdateFeatureFlagsRequestType;
}

export const fetchFeatureFlagsSuccess: (payload: FetchFeatureFlagsResponse) => FetchFeatureFlagsSuccessAction = (
    payload,
) => {
    return {
        type: FETCH_FEATURE_FLAGS_SUCCESS,
        payload,
    };
};

export const tryUpdateFeatureFlags: (
    type: FeatureFlagUpdatable,
    requestData: UpdateFeatureFlagsRequestDataType,
    id?: number | string,
) => TryUpdateFeatureFlagsAction = (type, requestData, id = null) => {
    return {
        type: type,
        payload: {
            requestData,
            id,
        },
    };
};

export const updateFeatureFlagsSuccess: (payload: UpdateFeatureFlagsResponse) => UpdateFeatureFlagsSuccessAction = (
    payload,
) => {
    return {
        type: UPDATE_FEATURE_FLAGS,
        payload,
    };
};

export interface FeatureFlagsUpdatedAction {
    type: typeof FEATURE_FLAGS_FOR_RELEASES_UPDATED;
    payload: FetchFeatureFlagsResponse[];
}

export const updateFeatureFlagsForReleases: (payload: FetchFeatureFlagsResponse[]) => FeatureFlagsUpdatedAction = (
    payload,
) => {
    return {
        type: FEATURE_FLAGS_FOR_RELEASES_UPDATED,
        payload,
    };
};

export const buildConditionValueText: (condition: FeatureFlagRuleCondition) => string = (condition) => {
    const convertedValues: string[] = condition.values.map((value) => {
        switch (condition.predicate) {
            case FeatureFlagPredicate.After:
            case FeatureFlagPredicate.Before:
                return dayjs(value as string).format(getLocaleDateTimeFormat('fullYearAtTime', false));
            default: {
                const stringValue = value as string;
                return stringValue ? stringValue : `${value}`;
            }
        }
    });
    return convertedValues.join(', ');
};

export const buildAudiencePredicateText: (predicate: FeatureFlagPredicate, negate: boolean) => string = (
    predicate,
    negate,
) => {
    switch (predicate) {
        case FeatureFlagPredicate.Matches:
            return `users ${negate ? 'not matching' : 'matching'}`;
        default:
            return buildPredicateText(predicate, negate);
    }
};

export const buildPredicateText: (predicate: FeatureFlagPredicate, negate: boolean) => string = (predicate, negate) => {
    const negatedIsNotModifier: (s: string) => string = (s) => {
        return `${negate ? 'is not' : 'is'} ${s}`;
    };
    switch (predicate) {
        case FeatureFlagPredicate.After:
            return negatedIsNotModifier('after');
        case FeatureFlagPredicate.Before:
            return negatedIsNotModifier('before');
        case FeatureFlagPredicate.Contains:
            return negate ? 'does not contain' : 'contains';
        case FeatureFlagPredicate.EndsWith:
            return negate ? 'does not end with ' : 'ends with';
        case FeatureFlagPredicate.GreaterThan:
            return negatedIsNotModifier('greater than');
        case FeatureFlagPredicate.GreaterThanOrEqual:
            return negatedIsNotModifier('greater than or equal to ');
        case FeatureFlagPredicate.LessThan:
            return negatedIsNotModifier('less than');
        case FeatureFlagPredicate.LessThanOrEqual:
            return negatedIsNotModifier('less than or equal to');
        case FeatureFlagPredicate.Matches:
            return negate ? 'does not match' : 'matches';
        case FeatureFlagPredicate.StartsWith:
            return negate ? 'does not start with' : 'starts with';
        default:
            return 'compare to';
    }
};
